import { useEffect, useRef, useState } from 'react';

// A departures-board title in the landing's own dotted title face: every letter runs through
// the alphabet before it lands, left to right, the way an airport board settles. It starts the
// first time it comes into view; with reduced motion it simply shows the text. `rows` breaks
// the text over lines on a phone (they join into one line from md up).

const ALPHABET = 'ABCDEFGHIJKLMNÑOPQRSTUVWXYZ0123456789';
const TICK_MS = 55; // one flap
const STAGGER = 3; // extra flaps per letter, left to right
const SPIN = 8; // flaps every letter turns through at least

export function SplitFlap({ rows, label, className = '' }: { rows: string[]; label: string; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const target = rows.join('\n');
  const [shown, setShown] = useState(target);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let tick = 0;
    let k = 0;
    const lands = [...target].map((c) => (/\s/.test(c) ? 0 : SPIN + STAGGER * k++ + Math.floor(Math.random() * 3)));
    const id = window.setInterval(() => {
      tick++;
      let done = true;
      setShown(
        [...target]
          .map((c, i) => {
            if (/\s/.test(c) || tick >= lands[i]) return c;
            done = false;
            return ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
          })
          .join(''),
      );
      if (done) window.clearInterval(id);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [started, target]);

  return (
    <h2 ref={ref} aria-label={label} className={`font-title uppercase leading-[0.9] ${className}`}>
      {shown.split('\n').map((row, r) => (
        // each letter in a fixed-width cell, so the line doesn't shimmy while they spin
        <span key={r} aria-hidden className="block whitespace-nowrap md:inline md:[&:not(:last-child)]:mr-[0.5em]">
          {[...row].map((c, i) => (
            <span key={i} className="inline-block w-[0.72em] text-center">
              {c}
            </span>
          ))}
        </span>
      ))}
    </h2>
  );
}
